// Agenti: le persone del team, da cui il PM istanzia gli agenti di ogni lavoro. Si leggono e si
// cambiano qui; le modifiche vanno nella configurazione (`agents.json`), sopra i valori di partenza.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { AgentProfile, ConnectedProvider, DefaultChoice, ToolGroup } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Pop } from '../../components/Pop';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonRow } from '../../components/Skeleton';
import { useStore } from '../../store/StoreProvider';
import { popRole } from '../../lib/pops';
import type { Tint } from '../../shell/nav';
import { AgentEditor, modelLabel } from './AgentEditor';

// le famiglie dei ruoli, con il loro colore pop (tela «PopTown Pop Style», Pops)
const FAMILY: { id: string; label: string; tint: Tint; roles: string[] }[] = [
  {
    id: 'lead',
    label: 'Gestione',
    tint: 'lilac',
    roles: ['project-manager', 'team-lead', 'scheduler'],
  },
  {
    id: 'dev',
    label: 'Sviluppo',
    tint: 'blue',
    roles: ['developer', 'qa', 'reviewer', 'devops', 'security'],
  },
  { id: 'design', label: 'Design', tint: 'pink', roles: ['web-designer', 'ux-ui'] },
  {
    id: 'data',
    label: 'Dati e ricerca',
    tint: 'mint',
    roles: ['analyst', 'data-scientist', 'researcher', 'triage'],
  },
  { id: 'words', label: 'Testi', tint: 'coral', roles: ['writer', 'translator'] },
  { id: 'service', label: 'Servizi', tint: 'sun', roles: ['notifier', 'mail-clerk', 'home'] },
];

export function familyOf(a: AgentProfile) {
  const role = popRole(a.avatar);
  return FAMILY.find((f) => f.roles.includes(role)) ?? FAMILY[0];
}

// Un pop come figurina: il pop grande su fondo colorato, il suo nome, il ruolo e il modello.
function AgentCard({ a, onOpen }: { a: AgentProfile; onOpen: () => void }) {
  const f = familyOf(a);
  return (
    <button type="button" onClick={onOpen} className="pop-card" data-tint={f.tint}>
      <span className="pop-card-stage">
        <span className="pop-card-family">{a.team ? `team ${a.team}` : f.label}</span>
        {a.coordinator && <span className="pop-card-lead">Coordinatore</span>}
        <Pop avatar={a.avatar} size={132} />
      </span>
      <span className="pop-card-body">
        <span className="pop-card-name">
          <span className="pop-name">{a.nickname}</span>
          <span className="pop-card-role">{a.label}</span>
        </span>
        <span className="pop-card-duty">{a.duty}</span>
        <span className="pop-card-foot">
          <span className="pop-card-model">
            <span className="pop-card-dot" aria-hidden />
            {a.effectiveModel ? modelLabel(a.effectiveModel) : 'nessun modello'}
          </span>
          <span className="pop-card-tools">
            {a.tools.length} {a.tools.length === 1 ? 'strumento' : 'strumenti'}
            {!a.thinking && ' · senza ragionamento'}
          </span>
        </span>
      </span>
    </button>
  );
}

export function Agents() {
  const { openModal, toast } = useStore();
  const [rows, setRows] = useState<AgentProfile[] | null>(null);
  const [groups, setGroups] = useState<ToolGroup[]>([]);
  const [providers, setProviders] = useState<ConnectedProvider[]>([]);
  const [defaultChoice, setDefaultChoice] = useState<DefaultChoice | null>(null);
  const [editing, setEditing] = useState<{ agent: AgentProfile | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [family, setFamily] = useState('all');

  const refresh = useCallback(async () => {
    try {
      setRows(await api.profiles());
    } catch {
      setError('Non riesco a leggere i Pops.');
    }
  }, []);

  useEffect(() => {
    void refresh();
    void api.tools().then(
      (c) => setGroups(c.groups),
      () => setGroups([]),
    );
    void api.providers().then(
      (p) => setProviders(p.providers),
      () => setProviders([]),
    );
    void api.defaultModel().then(setDefaultChoice, () => setDefaultChoice(null));
  }, [refresh]);

  // aprendo o chiudendo il form si riparte dall'alto della pagina
  useEffect(() => {
    document.querySelector('.page')?.scrollIntoView({ block: 'start' });
  }, [editing]);

  const remove = (a: AgentProfile) =>
    openModal({
      kind: 'confirm',
      title: `Togliere «${a.label}»?`,
      okLabel: 'Togli',
      danger: true,
      payload: {
        run: async () => {
          try {
            await api.removeProfile(a.id);
            setEditing(null);
            await refresh();
            toast({ kind: 'success', title: `${a.label}: tolto dal team` });
          } catch (e) {
            const body = e instanceof ApiError && e.detail ? e.detail : 'Riprova.';
            toast({ kind: 'danger', title: 'Non si toglie', body });
          }
        },
      },
    });

  const editor = editing && (
    <AgentEditor
      key={editing.agent?.id ?? 'new'}
      agent={editing.agent}
      groups={groups}
      providers={providers}
      defaultModel={defaultChoice?.resolved ?? null}
      defaultChoice={defaultChoice}
      takenIds={rows?.map((r) => r.id) ?? []}
      onClose={() => setEditing(null)}
      onRemove={remove}
      onSaved={(a) => {
        toast({ kind: 'success', title: `${a.label}: salvato` });
        setEditing(null);
        void refresh();
      }}
    />
  );

  // il form si apre nel corpo della pagina, al posto dell'elenco
  if (editor)
    return (
      <div className="page" style={{ maxWidth: 920 }}>
        <div style={{ marginBottom: 12 }}>
          <Button variant="ghost" icon="arrow-left" onClick={() => setEditing(null)}>
            Torna ai Pops
          </Button>
        </div>
        {editor}
      </div>
    );

  return (
    <div className="page">
      <PageHeader
        title="Pops"
        subtitle="I ruoli del team, come modelli: nel piano di un progetto ogni ruolo diventa uno o più membri, ognuno con il suo nome e il suo modello."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing({ agent: null })}>
            Nuovo pop
          </Button>
        }
      />
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Banner kind="danger">{error}</Banner>
        </div>
      )}
      <div role="tablist" aria-label="Famiglie di ruoli" className="pop-families">
        <button
          type="button"
          role="tab"
          aria-selected={family === 'all'}
          className="pop-family"
          data-on={family === 'all' || undefined}
          onClick={() => setFamily('all')}
        >
          Tutti · {rows?.length ?? 0}
        </button>
        {FAMILY.filter((f) => rows?.some((a) => familyOf(a).id === f.id)).map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={family === f.id}
            className="pop-family"
            data-tint={f.tint}
            data-on={family === f.id || undefined}
            onClick={() => setFamily(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="pop-grid">
        {rows === null && !error && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}
        {rows
          ?.filter((a) => family === 'all' || familyOf(a).id === family)
          .map((a) => (
            <AgentCard key={a.id} a={a} onOpen={() => setEditing({ agent: a })} />
          ))}
      </div>
    </div>
  );
}
